import { motion } from "framer-motion";
import { Stethoscope, ClipboardList, Activity, Users } from "lucide-react";

const highlights = [
  { icon: Stethoscope, label: "Konsultasi dokter hewan" },
  { icon: ClipboardList, label: "Pemeriksaan klinis" },
  { icon: Activity, label: "Monitoring kesehatan" },
  { icon: Users, label: "Edukasi owner" },
];
const ease = [0.2, 0.8, 0.2, 1];

export default function Poliklinik() {
  return (
    <section
      id="poliklinik"
      data-testid="poliklinik-section"
      className="relative py-24 md:py-32 bg-[var(--pikko-cream-deep)]/40"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease }}
          className="lg:col-span-7 order-2 lg:order-1"
        >
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-5 rounded-[40px] -z-10"
              style={{
                background:
                  "linear-gradient(160deg, rgba(217,140,63,0.16), transparent 70%)",
              }}
            />
            <div className="relative rounded-[28px] overflow-hidden shadow-[0_40px_100px_-50px_rgba(46,34,24,0.4)] border border-[rgba(138,107,82,0.15)]">
              <img
                src="/images/poliklinik.webp"
                alt="Konsultasi dan pemeriksaan kesehatan hewan di poliklinik Pikko Pet Care"
                className="w-full aspect-[4/3] object-cover warm-grade-strong"
                loading="lazy"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4, ease }}
              className="absolute -bottom-6 -right-2 sm:right-6 glass-light rounded-2xl px-5 py-4 shadow-[0_22px_50px_-25px_rgba(46,34,24,0.35)]"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pikko-brown)] font-semibold">
                Pemeriksaan Klinis
              </div>
              <div className="font-serif text-[18px] text-[var(--pikko-brown-ink)] leading-tight">
                Pendekatan medis yang teliti
              </div>
            </motion.div>
          </div>
        </motion.div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="eyebrow"
          >
            — 01 / Layanan
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, ease }}
            className="mt-4 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-[var(--pikko-brown-ink)]"
          >
            <span className="italic text-[var(--pikko-orange)]">Poli</span>klinik
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, delay: 0.1, ease }}
            className="mt-6 text-[15px] leading-relaxed text-[var(--pikko-brown-deep)]/85 max-w-xl"
          >
            Layanan konsultasi dan pemeriksaan kesehatan hewan dengan pendekatan
            medis profesional untuk membantu menjaga kondisi hewan tetap optimal.
          </motion.p>

          <div className="mt-9 grid grid-cols-2 gap-3.5">
            {highlights.map((h, i) => (
              <motion.div
                key={h.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 + i * 0.08, ease }}
                data-testid={`poliklinik-highlight-${i}`}
                className="flex items-start gap-3 p-4 rounded-2xl bg-[var(--pikko-cream-warm)] border border-[rgba(138,107,82,0.12)] hover:border-[var(--pikko-orange)]/40 transition-colors duration-300"
              >
                <div className="w-9 h-9 rounded-full bg-[var(--pikko-orange)]/12 flex items-center justify-center flex-shrink-0">
                  <h.icon className="w-4 h-4 text-[var(--pikko-orange-dark)]" />
                </div>
                <div className="text-[13px] leading-snug text-[var(--pikko-brown-deep)] font-medium pt-1">
                  {h.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
